// ═══════════════════════════════════════════════════════════════════════════
//  BAHASA REMOTE TAMU — id · en · zh · ja · ko
//
//  Zaldy 2 Okt 2026: pemilih bahasa di remote karaoke "kayak di member app".
//
//  Sengaja ditulis sendiri, sekecil mungkin, meniru member-app/src/i18n:
//    · daftar bahasa ditulis dalam bahasanya sendiri (endonim) + bendera —
//      tamu yang perlu mengganti bahasa justru sedang membaca layar dalam
//      bahasa yang tidak ia mengerti;
//    · kamus Indonesia = sumber kunci; kamus lain diketik Record<Kunci, string>
//      sehingga kunci yang lupa diterjemahkan GAGAL di `tsc`;
//    · saat berjalan tetap ada cadangan: bahasa terpilih → Indonesia → kunci.
//
//  Disimpan per perangkat di localStorage ('karaoke.bahasa'). HP tamu dan
//  tablet kamar masing-masing ingat pilihannya sendiri. TV (/p) tidak ikut —
//  ia perangkat terpisah tanpa pemilih, jadi tetap Bahasa Indonesia.
//
//  Judul lagu, nama artis, dan pesan galat dari server TIDAK diterjemahkan.
// ═══════════════════════════════════════════════════════════════════════════

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export type KodeBahasa = 'id' | 'en' | 'zh' | 'ja' | 'ko'

export const BAHASA_BAWAAN: KodeBahasa = 'id'

export type Bahasa = { kode: KodeBahasa; nama: string; bendera: string; locale: string }

// Endonim — TIDAK ikut diterjemahkan (sama dengan member-app/src/i18n/bahasa.ts).
export const DAFTAR_BAHASA: Bahasa[] = [
  { kode: 'id', nama: 'Bahasa Indonesia', bendera: '🇮🇩', locale: 'id-ID' },
  { kode: 'en', nama: 'English',          bendera: '🇬🇧', locale: 'en-GB' },
  { kode: 'zh', nama: '简体中文',          bendera: '🇨🇳', locale: 'zh-CN' },
  { kode: 'ja', nama: '日本語',            bendera: '🇯🇵', locale: 'ja-JP' },
  { kode: 'ko', nama: '한국어',            bendera: '🇰🇷', locale: 'ko-KR' },
]

export const cariBahasa = (kode: string | null | undefined): Bahasa =>
  DAFTAR_BAHASA.find(b => b.kode === kode) ?? DAFTAR_BAHASA[0]

const bahasaSah = (kode: string | null | undefined): KodeBahasa =>
  DAFTAR_BAHASA.some(b => b.kode === kode) ? (kode as KodeBahasa) : BAHASA_BAWAAN

// ── KAMUS INDONESIA — sumber kunci ─────────────────────────────────────────

const id = {
  // Tablet kamar
  'perangkat.tablet': 'Tablet kamar',
  'perangkat.belumTerhubung': 'Tablet belum terhubung',
  'perangkat.menunggu': 'Menunggu sesi dibuka',
  'perangkat.petunjuk': 'Begitu booking room ini dimulai, layar ini terbuka sendiri — tidak perlu memasukkan kode.',
  'perangkat.lepas': 'Lepas pasangan tablet',
  'perangkat.lepasTanya': 'Lepas pasangan tablet ini dari room? Remote akan kembali meminta kode.',

  // Gerbang kode
  'gerbang.judul': 'Karaoke',
  'gerbang.petunjuk': 'Ketik kode yang tampil di layar TV.',
  'gerbang.pendek': 'Kode terlalu pendek',
  'gerbang.masuk': 'Masuk',

  // Kepala
  'isi.hai': 'Hi',
  'isi.haiNama': 'Hi, {nama}',
  'isi.room': 'Room',
  'isi.dijeda': 'dijeda',
  'isi.habis': 'waktu habis',
  'isi.kode': 'kode',
  'isi.namaKamu': 'Nama kamu',
  'isi.gantiKode': 'Ganti kode',
  'isi.masukAntrean': '"{judul}" masuk antrean',

  // Tab bawah
  'tab.jelajah': 'Jelajah',
  'tab.antre': 'Antrean',
  'tab.layanan': 'Layanan',
  'tab.skor': 'Skor',
  'papan.judul': 'Papan skor',

  // Layanan
  'status.belum': 'Menunggu',
  'status.diproses': 'Disiapkan',
  'status.diantar': 'Diantar ✓',
  'layanan.dipanggil': 'Waitress dipanggil ✓ — segera datang ke room Anda.',
  'layanan.sudahDipanggil': 'Waitress sudah dipanggil — mohon tunggu sebentar.',
  'layanan.menuju': 'Waitress sedang menuju room',
  'layanan.panggil': 'Panggil Waitress',
  'layanan.marketing': 'Marketing Anda',
  'layanan.pesanan': 'Pesanan room ini',
  'layanan.kosong': 'Belum ada pesanan makanan / minuman.',
  'layanan.belumDatang': 'Belum datang',
  'layanan.sudahDatang': 'Sudah datang',
  'layanan.tanpaMarketing': 'Room ini belum punya marketing — silakan panggil waitress.',
  'layanan.menungguWaitress': 'Waitress dipanggil — menunggu konfirmasi',
  'layanan.panggilMarketing': 'Panggil Marketing',
  'layanan.marketingDipanggil': 'Marketing dipanggil ✓ — menunggu konfirmasi.',
  'layanan.marketingSudah': 'Marketing sudah dipanggil — mohon tunggu sebentar.',
  'layanan.marketingMenunggu': 'Marketing dipanggil — menunggu konfirmasi',
  'layanan.marketingMenuju': 'Marketing sedang menuju room',
  'layanan.marketingTakAda': 'Marketing room ini belum terhubung ke sistem — silakan hubungi lewat WhatsApp.',

  // Promo
  'promo.event': 'Event ElGroup',
  'promo.promo': 'Promo ElGroup',
  'promo.sambut': 'Selamat datang di El Karaoke',
  'promo.lihat': 'Lihat promo',
  'promo.petunjuk': 'Ketuk lagu dari Spotify, YouTube, atau favorit ElGroup — langsung diputar di TV.',

  // Jelajah
  'jelajah.judul': 'Top Lagu',
  'jelajah.cari': 'Cari lagu',
  'jelajah.ketSpotify': 'Top 50 Indonesia',
  'jelajah.ketYoutube': 'Trending musik',
  'jelajah.ketKami': 'Paling sering dinyanyikan di sini',
  'jelajah.ketCari': 'Judul atau penyanyi',
  'jelajah.namaSkor': 'Nama kamu (untuk papan skor)',
  'jelajah.tidakCocok': 'Tidak ada lagu yang cocok.',
  'jelajah.ketik': 'Ketik judul atau nama penyanyi.',
  'jelajah.lirik': 'lirik',
  'jelajah.skorNada': 'skor nada',
  'jelajah.memuat': 'Memuat…',
  'jelajah.kamiKosong': 'Belum ada yang dinyanyikan 60 hari terakhir — jadi yang pertama!',
  'jelajah.chartKosong': 'Chart belum tersedia hari ini.',
  'jelajah.diperbarui': 'Diperbarui {waktu}',
  'jelajah.belumKatalog': 'belum ada di katalog',

  // Antrean
  'antre.judul': 'Antrean',
  'antre.jumlah': '{n} lagu',
  'antre.geser': 'Tahan & geser untuk mengubah urutan',
  'antre.kosong': 'Antrean masih kosong — ketuk lagu mana pun, langsung diputar.',
  'antre.setelan': 'Nada / tempo / vokal',
  'antre.batalkan': 'Batalkan',
  'antre.sedang': 'Sedang diputar',
  'antre.belumPutar': 'Belum ada yang diputar',
  'antre.berikutnya': 'Berikutnya: {judul}',
  'antre.pilihKiri': 'Pilih lagu di sebelah kiri',
  'antre.selesai': 'Selesai',
  'antre.lewati': 'Lewati',
  'antre.putar': 'Putar sekarang',
  'kontrol.ulang': 'Ulang lagu ini',
  'kontrol.ulangNyala': 'Ulang lagu: NYALA — lagu ini diputar terus',
  'kontrol.ulangMati': 'Ulang lagu: mati',
  // 21 Okt 2026: geser lirik YouTube/Spotify dari remote (per video, tersimpan di server)
  'kontrol.lirik': 'Lirik',
  'kontrol.lirikPas': 'Lirik pas',
  'kontrol.lirikGeser': 'Lirik {n} dtk',
  'kontrol.lirikKet': 'Lirik YouTube disiapkan otomatis: PC karaoke mendengar videonya (Whisper) dan menyelaraskan teks resmi; video yang sudah ada liriknya di gambar dibiarkan tanpa lirik tambahan. Biasanya siap 1–2 menit setelah masuk antrean. Geser di sini kalau masih kurang pas — tersimpan untuk video ini di semua room.',
  'kontrol.lirikNyala': 'Lirik nyala',
  'kontrol.lirikMati': 'Lirik: disiapkan…',
  'kontrol.lirikBelum': 'Lirik video ini belum dicari TV — coba lagi setelah lagunya mulai',
  'antre.mulai': 'Mulai "{judul}"',

  // Setelan lagu
  'setelan.nada': 'Nada',
  'setelan.tempo': 'Tempo',
  'setelan.normal': 'Normal',
  'setelan.vokal': 'Vokal',
  'setelan.vokalNyala': 'Panduan vokal NYALA',
  'setelan.vokalMati': 'Panduan vokal mati',
  'setelan.tanpaInstrumental': 'Lagu ini belum punya trek instrumental terpisah — vokal dihilangkan dengan membatalkan kanal tengah, jadi bass dan snare ikut terdengar lebih tipis.',
  'setelan.acuan1': 'Belum ada nada acuan — skornya menilai ',
  'setelan.acuanIrama': 'ketepatan irama',
  'setelan.acuan2': ', bukan ketepatan nada.',
  'setelan.batalAntre': 'Batalkan dari antrean',
  "setelan.tempoTidakYt": "Tidak tersedia untuk YouTube",
  "setelan.ytCatatan": "Nada & vokal YouTube dipakai bila TV-nya PC dengan EL Audio aktif; vokal off = pengurang vokal (hasil tergantung lagu). Musik off = mute.",
  'nada.asli': 'Nada asli',

  // 3 Okt 2026 — katalog luar (Spotify/YouTube) + kontrol audio pemutar
  "luar.ketSpotify": "Kategori, album & cari",
  "luar.ketYoutube": "Cari & putar video musik di TV",
  "luar.cariSpotify": "Cari lagu, album, artis…",
  "luar.cariYoutube": "Cari di YouTube…",
  "luar.cari": "Cari",
  "luar.karaoke": "Karaoke",
  "luar.tanpaKunciSp": "Tanpa kunci Spotify: kategori dari playlist pilihan, pencarian lewat iTunes.",
  "luar.tanpaKunciYt": "Pencarian YouTube belum aktif (kunci API belum diisi). Chart tetap bisa diputar.",
  "luar.lagu": "Lagu",
  "luar.album": "Album",
  "luar.artis": "Artis",
  "luar.kembali": "Kembali",
  "luar.kosong": "Belum ada isi.",
  "luar.putarSpotify": "Putar di Spotify",
  "luar.putarTv": "Putar di TV",
  "luar.nyanyiKaraoke": "Nyanyi (karaoke)",
  "luar.cariVersiKaraoke": "Cari versi karaoke",
  "luar.adaKatalog": "ada versi karaoke",
  "luar.chart": "Chart",
  "luar.hasil": "Hasil cari",
  "luar.masukSpotify": "{judul} masuk antrean — diputar aplikasi Spotify di komputer karaoke",
  "luar.masukYoutube": "{judul} masuk antrean (YouTube)",
  "luar.kuotaCatatan": "Tekan Cari — kuota pencarian terbatas.",
  "luar.karaokeTop": "✓ Karaoke nyala: ketuk lagu di daftar → yang diputar versi karaokenya.",
  "luar.wantlist": "Daftar pesanan",
  "luar.tidakAdaHasil": "Tidak ada hasil.",
  "kontrol.judul": "Kontrol audio",
  "kontrol.vokal": "Vokal",
  "kontrol.musik": "Musik",
  "kontrol.nada": "Nada",
  "kontrol.on": "ON",
  "kontrol.off": "OFF",
  "kontrol.reset": "Reset",
  "kontrol.jeda": "Jeda",
  "kontrol.lanjut": "Lanjut",
  "kontrol.volume": "Volume",
  "kontrol.hanyaUpload": "Hanya untuk lagu upload",
  "kontrol.belumDiproses": "Belum diproses",
  "kontrol.spotifyLokal": "Diputar aplikasi Spotify di komputer karaoke — atur dari sana",
  "kontrol.tukarMusik": "Stem vokal belum ada — musik off membisukan semua",
  "kontrol.muteYt": "Musik off = mute",
  "kontrol.bukaSpotify": "Buka di Spotify",
  "kontrol.ytPerluElAudio": "Vokal & nada YouTube hanya di TV PC dengan ekstensi EL Audio aktif",
  "kontrol.ytPengurang": "YouTube: vokal off memakai pengurang vokal — hasilnya tergantung lagu",

  // Papan skor
  'papan.kosong1': 'Belum ada skor malam ini.',
  'papan.kosong2': 'Skor muncul setelah lagu selesai dinyanyikan.',
  'papan.baris': '{lagu} lagu · rata-rata {rerata}',
  'papan.campur': 'cara nilai campuran',

  // 5 Okt 2026 — remote: kategori bahasa, karaoke satu ketukan, bilah mengambang
  'kat.id': 'Indonesia',
  'kat.en': 'Inggris',
  'kat.zh': 'China',
  'kat.ja': 'Jepang',
  'kat.ko': 'Korea',
  'kat.semua': 'Semua',
  'genre.top': 'Top',
  'genre.pilih': 'Pilih genre',
  'genre.belum': 'Daftar genre ini belum tersedia — sedang disiapkan. Coba lagi nanti atau pilih Top.',
  'jelajah.ketSp2': 'Tangga lagu Spotify · ketuk = nyanyi versi karaoke',
  'jelajah.ketYt2': 'Musik trending YouTube · ketuk = putar di TV',
  'jelajah.ketCari2': 'Cari di katalog karaoke kami',
  'jelajah.cariSpLagu': 'Cari judul atau penyanyi di Spotify…',
  'jelajah.lencanaKaraoke': 'Karaoke',
  'karaoke.mencari': 'Mencari versi karaoke…',
  'karaoke.diputar': '🎤 "{judul}" versi karaoke — diputar sekarang',
  'karaoke.masuk': '🎤 "{judul}" versi karaoke — masuk antrean',
  'karaoke.kuota': 'Kuota pencarian YouTube hari ini habis. Pilih dari tab YouTube atau Top ElGroup.',
  'karaoke.kunci': 'Pencarian versi karaoke belum aktif di outlet ini.',
  'karaoke.tidakAda': 'Versi karaoke "{judul}" tidak ditemukan.',
  'isi.diputarSekarang': '"{judul}" diputar sekarang',
  'bilah.pilihLagu': 'Ketuk lagu mana pun — langsung diputar',
  'bilah.menungguSesi': 'Lagu mulai saat sesi berjalan',
  'bilah.kosongSub': 'Antrean kosong',
  'bilah.bahasa': 'Bahasa',
  'bilah.nama': 'Nama penyanyi (opsional, untuk papan skor)',
  'bilah.kontrol': 'Kontrol',
  'bilah.tutup': 'Tutup',
  'kontrol.dijeda': 'Dijeda',
  // 5 Okt 2026 sore: bilah putar ala Spotify.
  'kontrol.posisi': 'Posisi lagu',
  'kontrol.perkiraan': 'Perkiraan — Spotify diputar aplikasi di komputer karaoke',
  'kontrol.keterangan': 'Keterangan',
}

export type Kunci = keyof typeof id

const en: Record<Kunci, string> = {
  'perangkat.tablet': 'Room tablet',
  'perangkat.belumTerhubung': 'Tablet not connected',
  'perangkat.menunggu': 'Waiting for the session to open',
  'perangkat.petunjuk': 'As soon as this room’s booking starts, this screen opens by itself — no code needed.',
  'perangkat.lepas': 'Unpair this tablet',
  'perangkat.lepasTanya': 'Unpair this tablet from the room? The remote will ask for a code again.',

  'gerbang.judul': 'Karaoke',
  'gerbang.petunjuk': 'Enter the code shown on the TV screen.',
  'gerbang.pendek': 'Code is too short',
  'gerbang.masuk': 'Enter',

  'isi.hai': 'Hi',
  'isi.haiNama': 'Hi, {nama}',
  'isi.room': 'Room',
  'isi.dijeda': 'paused',
  'isi.habis': "time's up",
  'isi.kode': 'code',
  'isi.namaKamu': 'Your name',
  'isi.gantiKode': 'Change code',
  'isi.masukAntrean': '"{judul}" added to the queue',

  'tab.jelajah': 'Explore',
  'tab.antre': 'Queue',
  'tab.layanan': 'Service',
  'tab.skor': 'Scores',
  'papan.judul': 'Leaderboard',

  'status.belum': 'Pending',
  'status.diproses': 'Preparing',
  'status.diantar': 'Served ✓',
  'layanan.dipanggil': 'Waitress called ✓ — on the way to your room.',
  'layanan.sudahDipanggil': 'A waitress has already been called — please wait a moment.',
  'layanan.menuju': 'Waitress is on the way',
  'layanan.panggil': 'Call Waitress',
  'layanan.marketing': 'Your host',
  'layanan.pesanan': 'Orders for this room',
  'layanan.kosong': 'No food or drink orders yet.',
  'layanan.belumDatang': 'Not arrived yet',
  'layanan.sudahDatang': 'Arrived',
  'layanan.tanpaMarketing': 'This room has no host assigned — please call a waitress.',
  'layanan.menungguWaitress': 'Waitress called — waiting for confirmation',
  'layanan.panggilMarketing': 'Call Host',
  'layanan.marketingDipanggil': 'Host called ✓ — waiting for confirmation.',
  'layanan.marketingSudah': 'Your host has already been called — please wait a moment.',
  'layanan.marketingMenunggu': 'Host called — waiting for confirmation',
  'layanan.marketingMenuju': 'Your host is on the way',
  'layanan.marketingTakAda': 'Your host is not connected to the system yet — please contact them via WhatsApp.',

  'promo.event': 'ElGroup Event',
  'promo.promo': 'ElGroup Promo',
  'promo.sambut': 'Welcome to El Karaoke',
  'promo.lihat': 'View promo',
  'promo.petunjuk': 'Tap a song from Spotify, YouTube or ElGroup favourites — it plays on the TV right away.',

  'jelajah.judul': 'Top Songs',
  'jelajah.cari': 'Search',
  'jelajah.ketSpotify': 'Top 50 Indonesia',
  'jelajah.ketYoutube': 'Trending music',
  'jelajah.ketKami': 'Most sung here',
  'jelajah.ketCari': 'Title or artist',
  'jelajah.namaSkor': 'Your name (for the leaderboard)',
  'jelajah.tidakCocok': 'No matching songs.',
  'jelajah.ketik': 'Type a song title or artist name.',
  'jelajah.lirik': 'lyrics',
  'jelajah.skorNada': 'pitch scoring',
  'jelajah.memuat': 'Loading…',
  'jelajah.kamiKosong': 'Nothing sung here in the last 60 days — be the first!',
  'jelajah.chartKosong': 'Chart not available today.',
  'jelajah.diperbarui': 'Updated {waktu}',
  'jelajah.belumKatalog': 'not in catalogue yet',

  'antre.judul': 'Queue',
  'antre.jumlah': '{n} songs',
  'antre.geser': 'Hold & drag to reorder',
  'antre.kosong': 'The queue is empty — tap any song and it plays right away.',
  'antre.setelan': 'Key / tempo / vocals',
  'antre.batalkan': 'Remove',
  'antre.sedang': 'Now playing',
  'antre.belumPutar': 'Nothing playing yet',
  'antre.berikutnya': 'Up next: {judul}',
  'antre.pilihKiri': 'Pick a song on the left',
  'antre.selesai': 'Finish',
  'antre.lewati': 'Skip',
  'antre.putar': 'Play now',
  'kontrol.ulang': 'Repeat this song',
  'kontrol.ulangNyala': 'Repeat: ON — this song plays again and again',
  'kontrol.ulangMati': 'Repeat: off',
  'kontrol.lirik': 'Lyrics',
  'kontrol.lirikPas': 'Lyrics in sync',
  'kontrol.lirikGeser': 'Lyrics {n} s',
  'kontrol.lirikKet': 'YouTube lyrics are prepared automatically: the karaoke PC listens to the video (Whisper) and aligns the official text; videos that already show lyrics get none on top. Usually ready 1–2 min after queuing. Shift here if still off — saved for this video in all rooms.',
  'kontrol.lirikNyala': 'Lyrics on',
  'kontrol.lirikMati': 'Lyrics: preparing…',
  'kontrol.lirikBelum': 'The TV has not fetched lyrics for this video yet — try again once the song starts',
  'antre.mulai': 'Start "{judul}"',

  'setelan.nada': 'Key',
  'setelan.tempo': 'Tempo',
  'setelan.normal': 'Normal',
  'setelan.vokal': 'Vocals',
  'setelan.vokalNyala': 'Guide vocals ON',
  'setelan.vokalMati': 'Guide vocals off',
  'setelan.tanpaInstrumental': 'This song has no separate instrumental track yet — vocals are removed by cancelling the centre channel, so bass and snare also sound thinner.',
  'setelan.acuan1': 'No reference melody yet — the score rates ',
  'setelan.acuanIrama': 'rhythm accuracy',
  'setelan.acuan2': ', not pitch accuracy.',
  'setelan.batalAntre': 'Remove from queue',
  "setelan.tempoTidakYt": "Not available for YouTube",
  "setelan.ytCatatan": "YouTube key & vocals apply when the TV is a PC with EL Audio active; vocals off = vocal reducer (results vary by song). Music off = mute.",
  'nada.asli': 'Original key',

  // 3 Okt 2026 — katalog luar (Spotify/YouTube) + kontrol audio pemutar
  "luar.ketSpotify": "Categories, albums & search",
  "luar.ketYoutube": "Search & play music videos on the TV",
  "luar.cariSpotify": "Search songs, albums, artists…",
  "luar.cariYoutube": "Search YouTube…",
  "luar.cari": "Search",
  "luar.karaoke": "Karaoke",
  "luar.tanpaKunciSp": "No Spotify key: categories from curated playlists, search via iTunes.",
  "luar.tanpaKunciYt": "YouTube search is not active yet (API key not set). Charts can still be played.",
  "luar.lagu": "Songs",
  "luar.album": "Albums",
  "luar.artis": "Artists",
  "luar.kembali": "Back",
  "luar.kosong": "Nothing here yet.",
  "luar.putarSpotify": "Play on Spotify",
  "luar.putarTv": "Play on TV",
  "luar.nyanyiKaraoke": "Sing (karaoke)",
  "luar.cariVersiKaraoke": "Find karaoke version",
  "luar.adaKatalog": "karaoke version available",
  "luar.chart": "Chart",
  "luar.hasil": "Search results",
  "luar.masukSpotify": "{judul} queued — plays in the Spotify app on the karaoke computer",
  "luar.masukYoutube": "{judul} queued (YouTube)",
  "luar.kuotaCatatan": "Press Search — search quota is limited.",
  "luar.karaokeTop": "✓ Karaoke on: tap a song in the list → its karaoke version is queued.",
  "luar.wantlist": "Wishlist",
  "luar.tidakAdaHasil": "No results.",
  "kontrol.judul": "Audio controls",
  "kontrol.vokal": "Vocals",
  "kontrol.musik": "Music",
  "kontrol.nada": "Key",
  "kontrol.on": "ON",
  "kontrol.off": "OFF",
  "kontrol.reset": "Reset",
  "kontrol.jeda": "Pause",
  "kontrol.lanjut": "Resume",
  "kontrol.volume": "Volume",
  "kontrol.hanyaUpload": "Uploaded songs only",
  "kontrol.belumDiproses": "Not processed yet",
  "kontrol.spotifyLokal": "Played by the Spotify app on the karaoke computer — control it there",
  "kontrol.tukarMusik": "No vocal stem yet — music off mutes everything",
  "kontrol.muteYt": "Music off = mute",
  "kontrol.bukaSpotify": "Open in Spotify",
  "kontrol.ytPerluElAudio": "YouTube vocals & key only work on a PC TV with the EL Audio extension active",
  "kontrol.ytPengurang": "YouTube: vocals off uses a vocal reducer — results depend on the song",

  'papan.kosong1': 'No scores yet tonight.',
  'papan.kosong2': 'Scores appear once a song is finished.',
  'papan.baris': '{lagu} songs · avg {rerata}',
  'papan.campur': 'mixed scoring',

  // 5 Okt 2026 — remote: kategori bahasa, karaoke satu ketukan, bilah mengambang
  'kat.id': 'Indonesian',
  'kat.en': 'English',
  'kat.zh': 'Chinese',
  'kat.ja': 'Japanese',
  'kat.ko': 'Korean',
  'kat.semua': 'All',
  'genre.top': 'Top',
  'genre.pilih': 'Choose genre',
  'genre.belum': 'This genre list isn\'t ready yet — it\'s being prepared. Try again later or pick Top.',
  'jelajah.ketSp2': 'Spotify charts · tap = sing the karaoke version',
  'jelajah.ketYt2': 'Trending on YouTube · tap = play on the TV',
  'jelajah.ketCari2': 'Search our karaoke catalogue',
  'jelajah.cariSpLagu': 'Search a title or artist on Spotify…',
  'jelajah.lencanaKaraoke': 'Karaoke',
  'karaoke.mencari': 'Finding the karaoke version…',
  'karaoke.diputar': '🎤 "{judul}" karaoke version — playing now',
  'karaoke.masuk': '🎤 "{judul}" karaoke version — added to the queue',
  'karaoke.kuota': 'Today\'s YouTube search quota is used up. Pick from the YouTube or Top ElGroup tab.',
  'karaoke.kunci': 'Karaoke-version search is not active at this outlet yet.',
  'karaoke.tidakAda': 'No karaoke version found for "{judul}".',
  'isi.diputarSekarang': '"{judul}" is playing now',
  'bilah.pilihLagu': 'Tap any song — it plays right away',
  'bilah.menungguSesi': 'Songs start once the session is running',
  'bilah.kosongSub': 'Queue is empty',
  'bilah.bahasa': 'Language',
  'bilah.nama': 'Singer name (optional, for the leaderboard)',
  'bilah.kontrol': 'Controls',
  'bilah.tutup': 'Close',
  'kontrol.dijeda': 'Paused',
  'kontrol.posisi': 'Song position',
  'kontrol.perkiraan': 'Estimate — Spotify plays in the app on the karaoke computer',
  'kontrol.keterangan': 'Info',
}

const zh: Record<Kunci, string> = {
  'perangkat.tablet': '包厢平板',
  'perangkat.belumTerhubung': '平板尚未连接',
  'perangkat.menunggu': '等待开房',
  'perangkat.petunjuk': '本包厢的预订开始后，此画面会自动打开，无需输入代码。',
  'perangkat.lepas': '解除平板配对',
  'perangkat.lepasTanya': '要解除此平板与包厢的配对吗？遥控器将重新要求输入代码。',

  'gerbang.judul': '卡拉OK',
  'gerbang.petunjuk': '请输入电视屏幕上显示的代码。',
  'gerbang.pendek': '代码太短',
  'gerbang.masuk': '进入',

  'isi.hai': '你好',
  'isi.haiNama': '你好，{nama}',
  'isi.room': '包厢',
  'isi.dijeda': '已暂停',
  'isi.habis': '时间已到',
  'isi.kode': '代码',
  'isi.namaKamu': '你的名字',
  'isi.gantiKode': '更换代码',
  'isi.masukAntrean': '“{judul}”已加入已点列表',

  'tab.jelajah': '发现',
  'tab.antre': '已点',
  'tab.layanan': '服务',
  'tab.skor': '评分',
  'papan.judul': '排行榜',

  'status.belum': '等待中',
  'status.diproses': '准备中',
  'status.diantar': '已送达 ✓',
  'layanan.dipanggil': '已呼叫服务员 ✓ — 马上到您的包厢。',
  'layanan.sudahDipanggil': '已经呼叫过服务员，请稍候。',
  'layanan.menuju': '服务员正在赶来',
  'layanan.panggil': '呼叫服务员',
  'layanan.marketing': '您的专属客户经理',
  'layanan.pesanan': '本包厢订单',
  'layanan.kosong': '暂无餐饮订单。',
  'layanan.belumDatang': '尚未送达',
  'layanan.sudahDatang': '已送达',
  'layanan.tanpaMarketing': '该包厢尚未指定客户经理 — 请呼叫服务员。',
  'layanan.menungguWaitress': '已呼叫服务员 — 等待确认',
  'layanan.panggilMarketing': '呼叫客户经理',
  'layanan.marketingDipanggil': '已呼叫客户经理 ✓ — 等待确认。',
  'layanan.marketingSudah': '已经呼叫过客户经理，请稍候。',
  'layanan.marketingMenunggu': '已呼叫客户经理 — 等待确认',
  'layanan.marketingMenuju': '客户经理正在赶来',
  'layanan.marketingTakAda': '该包厢的客户经理尚未接入系统 — 请通过 WhatsApp 联系。',

  'promo.event': 'ElGroup 活动',
  'promo.promo': 'ElGroup 优惠',
  'promo.sambut': '欢迎来到 El Karaoke',
  'promo.lihat': '查看优惠',
  'promo.petunjuk': '从 Spotify、YouTube 或 ElGroup 热门歌曲中点一首，电视上立即播放。',

  'jelajah.judul': '热门歌曲',
  'jelajah.cari': '搜索歌曲',
  'jelajah.ketSpotify': '印尼 Top 50',
  'jelajah.ketYoutube': '热门音乐',
  'jelajah.ketKami': '本店最常点唱',
  'jelajah.ketCari': '歌名或歌手',
  'jelajah.namaSkor': '你的名字（用于排行榜）',
  'jelajah.tidakCocok': '没有找到匹配的歌曲。',
  'jelajah.ketik': '请输入歌名或歌手名。',
  'jelajah.lirik': '歌词',
  'jelajah.skorNada': '音准评分',
  'jelajah.memuat': '加载中…',
  'jelajah.kamiKosong': '近 60 天还没有人点唱，快来当第一个！',
  'jelajah.chartKosong': '今日榜单暂未更新。',
  'jelajah.diperbarui': '更新于 {waktu}',
  'jelajah.belumKatalog': '曲库暂未收录',

  'antre.judul': '已点歌曲',
  'antre.jumlah': '{n} 首',
  'antre.geser': '按住并拖动以调整顺序',
  'antre.kosong': '还没有点歌 — 点任意一首歌即可立即播放。',
  'antre.setelan': '音调 / 速度 / 人声',
  'antre.batalkan': '取消',
  'antre.sedang': '正在播放',
  'antre.belumPutar': '暂无播放',
  'antre.berikutnya': '下一首：{judul}',
  'antre.pilihKiri': '请在左侧选歌',
  'antre.selesai': '结束',
  'antre.lewati': '切歌',
  'antre.putar': '立即播放',
  'kontrol.ulang': '单曲循环',
  'kontrol.ulangNyala': '单曲循环：开启',
  'kontrol.ulangMati': '单曲循环：关闭',
  'kontrol.lirik': '歌词',
  'kontrol.lirikPas': '歌词同步',
  'kontrol.lirikGeser': '歌词 {n} 秒',
  'kontrol.lirikKet': 'YouTube歌词自动准备：卡拉OK电脑听取视频（Whisper）并对齐正式歌词；画面已有歌词的视频不再叠加。通常排队后1–2分钟就绪。若仍不准可在此微调，对所有包房生效。',
  'kontrol.lirikNyala': '歌词开',
  'kontrol.lirikMati': '歌词：准备中…',
  'kontrol.lirikBelum': '电视尚未获取此视频的歌词，歌曲开始后再试',
  'antre.mulai': '开始“{judul}”',

  'setelan.nada': '音调',
  'setelan.tempo': '速度',
  'setelan.normal': '正常',
  'setelan.vokal': '人声',
  'setelan.vokalNyala': '导唱：开',
  'setelan.vokalMati': '导唱：关',
  'setelan.tanpaInstrumental': '这首歌还没有单独的伴奏音轨，人声是通过消除中置声道去掉的，因此贝斯和军鼓也会变弱。',
  'setelan.acuan1': '暂无参考旋律，评分依据的是',
  'setelan.acuanIrama': '节奏准确度',
  'setelan.acuan2': '，而不是音准。',
  'setelan.batalAntre': '从已点列表移除',
  "setelan.tempoTidakYt": "YouTube 不支持",
  "setelan.ytCatatan": "电视为启用 EL Audio 的电脑时，YouTube 的音调和人声设置才会生效；关闭人声 = 人声消除（效果因歌曲而异）。关闭伴奏 = 静音。",
  'nada.asli': '原调',

  // 3 Okt 2026 — katalog luar (Spotify/YouTube) + kontrol audio pemutar
  "luar.ketSpotify": "分类、专辑与搜索",
  "luar.ketYoutube": "搜索并在电视上播放音乐视频",
  "luar.cariSpotify": "搜索歌曲、专辑、歌手…",
  "luar.cariYoutube": "搜索 YouTube…",
  "luar.cari": "搜索",
  "luar.karaoke": "卡拉OK",
  "luar.tanpaKunciSp": "未设置 Spotify 密钥：分类来自精选歌单，搜索使用 iTunes。",
  "luar.tanpaKunciYt": "YouTube 搜索尚未启用（未设置 API 密钥）。榜单仍可播放。",
  "luar.lagu": "歌曲",
  "luar.album": "专辑",
  "luar.artis": "歌手",
  "luar.kembali": "返回",
  "luar.kosong": "暂无内容。",
  "luar.putarSpotify": "在 Spotify 播放",
  "luar.putarTv": "在电视播放",
  "luar.nyanyiKaraoke": "演唱（卡拉OK）",
  "luar.cariVersiKaraoke": "查找卡拉OK版",
  "luar.adaKatalog": "有卡拉OK版",
  "luar.chart": "榜单",
  "luar.hasil": "搜索结果",
  "luar.masukSpotify": "{judul} 已加入队列 — 由卡拉OK电脑上的 Spotify 播放",
  "luar.masukYoutube": "{judul} 已加入队列（YouTube）",
  "luar.kuotaCatatan": "请按搜索 — 搜索次数有限。",
  "luar.karaokeTop": "✓ 卡拉OK 已开：点歌单中的歌曲 → 播放卡拉OK版本。",
  "luar.wantlist": "采购清单",
  "luar.tidakAdaHasil": "没有结果。",
  "kontrol.judul": "音频控制",
  "kontrol.vokal": "人声",
  "kontrol.musik": "伴奏",
  "kontrol.nada": "音调",
  "kontrol.on": "开",
  "kontrol.off": "关",
  "kontrol.reset": "重置",
  "kontrol.jeda": "暂停",
  "kontrol.lanjut": "继续",
  "kontrol.volume": "音量",
  "kontrol.hanyaUpload": "仅限上传的歌曲",
  "kontrol.belumDiproses": "尚未处理",
  "kontrol.spotifyLokal": "由卡拉OK电脑上的 Spotify 播放 — 请在那里调整",
  "kontrol.tukarMusik": "尚无人声音轨 — 关闭伴奏将全部静音",
  "kontrol.muteYt": "关闭伴奏 = 静音",
  "kontrol.bukaSpotify": "在 Spotify 打开",
  "kontrol.ytPerluElAudio": "YouTube 的人声和音调仅在启用 EL Audio 扩展的电脑电视上可用",
  "kontrol.ytPengurang": "YouTube：关闭人声使用人声消除 — 效果因歌曲而异",

  'papan.kosong1': '今晚还没有评分。',
  'papan.kosong2': '歌曲唱完后会显示评分。',
  'papan.baris': '{lagu} 首 · 平均 {rerata}',
  'papan.campur': '混合评分方式',

  // 5 Okt 2026 — remote: kategori bahasa, karaoke satu ketukan, bilah mengambang
  'kat.id': '印尼',
  'kat.en': '英语',
  'kat.zh': '华语',
  'kat.ja': '日语',
  'kat.ko': '韩语',
  'kat.semua': '全部',
  'genre.top': '热门',
  'genre.pilih': '选择曲风',
  'genre.belum': '该曲风榜单尚未就绪，正在准备中。请稍后再试或选择“热门”。',
  'jelajah.ketSp2': 'Spotify 排行榜 · 点一下 = 唱伴奏版',
  'jelajah.ketYt2': 'YouTube 热门音乐 · 点一下 = 电视播放',
  'jelajah.ketCari2': '搜索我们的 K 歌曲库',
  'jelajah.cariSpLagu': '在 Spotify 搜索歌名或歌手…',
  'jelajah.lencanaKaraoke': '伴奏',
  'karaoke.mencari': '正在寻找伴奏版…',
  'karaoke.diputar': '🎤 「{judul}」伴奏版 — 正在播放',
  'karaoke.masuk': '🎤 「{judul}」伴奏版 — 已加入点歌列表',
  'karaoke.kuota': '今日 YouTube 搜索额度已用完。请从 YouTube 或 Top ElGroup 选歌。',
  'karaoke.kunci': '本店尚未开启伴奏版搜索。',
  'karaoke.tidakAda': '找不到「{judul}」的伴奏版。',
  'isi.diputarSekarang': '「{judul}」正在播放',
  'bilah.pilihLagu': '点任意一首歌 — 立即播放',
  'bilah.menungguSesi': '包厢计时开始后才会播放',
  'bilah.kosongSub': '点歌列表为空',
  'bilah.bahasa': '语言',
  'bilah.nama': '演唱者姓名（可选，用于排行榜）',
  'bilah.kontrol': '控制',
  'bilah.tutup': '关闭',
  'kontrol.dijeda': '已暂停',
  'kontrol.posisi': '播放进度',
  'kontrol.perkiraan': '估计值 — Spotify 由卡拉OK电脑上的应用播放',
  'kontrol.keterangan': '说明',
}

const ja: Record<Kunci, string> = {
  'perangkat.tablet': 'ルーム端末',
  'perangkat.belumTerhubung': 'タブレットが未接続です',
  'perangkat.menunggu': 'セッション開始を待っています',
  'perangkat.petunjuk': 'このルームの予約が始まると、この画面が自動で開きます。コード入力は不要です。',
  'perangkat.lepas': 'タブレットのペアリング解除',
  'perangkat.lepasTanya': 'このタブレットとルームのペアリングを解除しますか？リモコンは再びコード入力を求めます。',

  'gerbang.judul': 'カラオケ',
  'gerbang.petunjuk': 'テレビ画面に表示されたコードを入力してください。',
  'gerbang.pendek': 'コードが短すぎます',
  'gerbang.masuk': '入室',

  'isi.hai': 'こんにちは',
  'isi.haiNama': 'こんにちは、{nama}さん',
  'isi.room': 'ルーム',
  'isi.dijeda': '一時停止中',
  'isi.habis': '時間終了',
  'isi.kode': 'コード',
  'isi.namaKamu': 'お名前',
  'isi.gantiKode': 'コード変更',
  'isi.masukAntrean': '「{judul}」を予約しました',

  'tab.jelajah': 'さがす',
  'tab.antre': '予約',
  'tab.layanan': 'サービス',
  'tab.skor': 'スコア',
  'papan.judul': 'ランキング',

  'status.belum': '受付待ち',
  'status.diproses': '準備中',
  'status.diantar': 'お届け済み ✓',
  'layanan.dipanggil': 'スタッフを呼びました ✓ まもなくお部屋に伺います。',
  'layanan.sudahDipanggil': 'すでにスタッフを呼んでいます。少々お待ちください。',
  'layanan.menuju': 'スタッフが向かっています',
  'layanan.panggil': 'スタッフを呼ぶ',
  'layanan.marketing': '担当スタッフ',
  'layanan.pesanan': 'このルームの注文',
  'layanan.kosong': 'まだフード・ドリンクの注文はありません。',
  'layanan.belumDatang': '未到着',
  'layanan.sudahDatang': '到着済み',
  'layanan.tanpaMarketing': 'このルームには担当スタッフがいません。スタッフを呼んでください。',
  'layanan.menungguWaitress': 'スタッフを呼びました — 確認待ち',
  'layanan.panggilMarketing': '担当スタッフを呼ぶ',
  'layanan.marketingDipanggil': '担当スタッフを呼びました ✓ 確認待ちです。',
  'layanan.marketingSudah': 'すでに担当スタッフを呼んでいます。少々お待ちください。',
  'layanan.marketingMenunggu': '担当スタッフを呼びました — 確認待ち',
  'layanan.marketingMenuju': '担当スタッフが向かっています',
  'layanan.marketingTakAda': 'このルームの担当スタッフはまだシステムに接続されていません。WhatsAppでご連絡ください。',

  'promo.event': 'ElGroup イベント',
  'promo.promo': 'ElGroup キャンペーン',
  'promo.sambut': 'El Karaoke へようこそ',
  'promo.lihat': '詳しく見る',
  'promo.petunjuk': 'Spotify・YouTube・ElGroup 人気曲から曲をタップすると、すぐにテレビで再生されます。',

  'jelajah.judul': '人気曲',
  'jelajah.cari': '曲を検索',
  'jelajah.ketSpotify': 'インドネシア Top 50',
  'jelajah.ketYoutube': '急上昇の音楽',
  'jelajah.ketKami': 'この店でよく歌われている曲',
  'jelajah.ketCari': '曲名またはアーティスト',
  'jelajah.namaSkor': 'お名前（ランキング用）',
  'jelajah.tidakCocok': '該当する曲がありません。',
  'jelajah.ketik': '曲名またはアーティスト名を入力してください。',
  'jelajah.lirik': '歌詞',
  'jelajah.skorNada': '音程採点',
  'jelajah.memuat': '読み込み中…',
  'jelajah.kamiKosong': '過去 60 日間まだ誰も歌っていません。最初の一曲をどうぞ！',
  'jelajah.chartKosong': '本日のチャートはまだありません。',
  'jelajah.diperbarui': '更新 {waktu}',
  'jelajah.belumKatalog': '未収録',

  'antre.judul': '予約リスト',
  'antre.jumlah': '{n} 曲',
  'antre.geser': '長押しして並べ替え',
  'antre.kosong': '予約はまだありません。曲をタップするとすぐに再生されます。',
  'antre.setelan': 'キー / テンポ / ボーカル',
  'antre.batalkan': '取り消し',
  'antre.sedang': '再生中',
  'antre.belumPutar': '再生中の曲はありません',
  'antre.berikutnya': '次の曲：{judul}',
  'antre.pilihKiri': '左から曲を選んでください',
  'antre.selesai': '終了',
  'antre.lewati': 'スキップ',
  'antre.putar': '今すぐ再生',
  'kontrol.ulang': 'この曲をリピート',
  'kontrol.ulangNyala': 'リピート：オン',
  'kontrol.ulangMati': 'リピート：オフ',
  'kontrol.lirik': '歌詞',
  'kontrol.lirikPas': '歌詞ぴったり',
  'kontrol.lirikGeser': '歌詞 {n} 秒',
  'kontrol.lirikKet': 'YouTube歌詞は自動準備：カラオケPCが動画を聴き（Whisper）正式な歌詞を同期。画面に歌詞がある動画は重ねない。通常キュー後1〜2分で準備完了。ずれはここで調整、全ルームに保存。',
  'kontrol.lirikNyala': '歌詞オン',
  'kontrol.lirikMati': '歌詞：準備中…',
  'kontrol.lirikBelum': 'テレビがまだこの動画の歌詞を取得していません。曲が始まってからもう一度',
  'antre.mulai': '「{judul}」を開始',

  'setelan.nada': 'キー',
  'setelan.tempo': 'テンポ',
  'setelan.normal': '標準',
  'setelan.vokal': 'ボーカル',
  'setelan.vokalNyala': 'ガイドボーカル ON',
  'setelan.vokalMati': 'ガイドボーカル OFF',
  'setelan.tanpaInstrumental': 'この曲にはまだ専用のカラオケ音源がありません。センターチャンネルを打ち消してボーカルを消しているため、ベースやスネアも少し薄く聞こえます。',
  'setelan.acuan1': '参考メロディがまだないため、採点は',
  'setelan.acuanIrama': 'リズムの正確さ',
  'setelan.acuan2': 'で行われます（音程は採点されません）。',
  'setelan.batalAntre': '予約を取り消す',
  "setelan.tempoTidakYt": "YouTube では使えません",
  "setelan.ytCatatan": "TV が EL Audio 有効の PC の場合に YouTube のキーとボーカル設定が反映されます。ボーカルオフ = ボーカル低減（効果は曲次第）。伴奏オフ = ミュート。",
  'nada.asli': '原曲キー',

  // 3 Okt 2026 — katalog luar (Spotify/YouTube) + kontrol audio pemutar
  "luar.ketSpotify": "カテゴリ・アルバム・検索",
  "luar.ketYoutube": "音楽ビデオを検索してテレビで再生",
  "luar.cariSpotify": "曲・アルバム・アーティストを検索…",
  "luar.cariYoutube": "YouTube を検索…",
  "luar.cari": "検索",
  "luar.karaoke": "カラオケ",
  "luar.tanpaKunciSp": "Spotify キー未設定：カテゴリは厳選プレイリスト、検索は iTunes を使用。",
  "luar.tanpaKunciYt": "YouTube 検索はまだ無効です（API キー未設定）。チャートは再生できます。",
  "luar.lagu": "曲",
  "luar.album": "アルバム",
  "luar.artis": "アーティスト",
  "luar.kembali": "戻る",
  "luar.kosong": "まだありません。",
  "luar.putarSpotify": "Spotify で再生",
  "luar.putarTv": "テレビで再生",
  "luar.nyanyiKaraoke": "歌う（カラオケ）",
  "luar.cariVersiKaraoke": "カラオケ版を探す",
  "luar.adaKatalog": "カラオケ版あり",
  "luar.chart": "チャート",
  "luar.hasil": "検索結果",
  "luar.masukSpotify": "{judul} を予約 — カラオケ PC の Spotify で再生",
  "luar.masukYoutube": "{judul} を予約（YouTube）",
  "luar.kuotaCatatan": "検索を押してください — 検索回数に上限があります。",
  "luar.karaokeTop": "✓ カラオケON：リストの曲をタップ → カラオケ版を再生。",
  "luar.wantlist": "購入リスト",
  "luar.tidakAdaHasil": "結果がありません。",
  "kontrol.judul": "オーディオ操作",
  "kontrol.vokal": "ボーカル",
  "kontrol.musik": "伴奏",
  "kontrol.nada": "キー",
  "kontrol.on": "オン",
  "kontrol.off": "オフ",
  "kontrol.reset": "リセット",
  "kontrol.jeda": "一時停止",
  "kontrol.lanjut": "再開",
  "kontrol.volume": "音量",
  "kontrol.hanyaUpload": "アップロード曲のみ",
  "kontrol.belumDiproses": "未処理",
  "kontrol.spotifyLokal": "カラオケ PC の Spotify アプリで再生中 — そちらで操作してください",
  "kontrol.tukarMusik": "ボーカル音源がまだありません — 伴奏オフで全体がミュート",
  "kontrol.muteYt": "伴奏オフ = ミュート",
  "kontrol.bukaSpotify": "Spotify で開く",
  "kontrol.ytPerluElAudio": "YouTube のボーカル・キー操作は EL Audio 拡張機能が有効な PC の TV でのみ使えます",
  "kontrol.ytPengurang": "YouTube：ボーカルオフはボーカル低減処理です — 効果は曲によって異なります",

  'papan.kosong1': '今夜のスコアはまだありません。',
  'papan.kosong2': '曲を歌い終えるとスコアが表示されます。',
  'papan.baris': '{lagu} 曲 · 平均 {rerata}',
  'papan.campur': '採点方式が混在',

  // 5 Okt 2026 — remote: kategori bahasa, karaoke satu ketukan, bilah mengambang
  'kat.id': 'インドネシア',
  'kat.en': '洋楽',
  'kat.zh': '中国語',
  'kat.ja': '邦楽',
  'kat.ko': '韓国',
  'kat.semua': 'すべて',
  'genre.top': 'トップ',
  'genre.pilih': 'ジャンルを選択',
  'genre.belum': 'このジャンルのリストはまだ準備中です。後でもう一度お試しいただくか、「トップ」をお選びください。',
  'jelajah.ketSp2': 'Spotify ランキング · タップでカラオケ版を歌う',
  'jelajah.ketYt2': 'YouTube の人気音楽 · タップでテレビ再生',
  'jelajah.ketCari2': '当店のカラオケ曲を検索',
  'jelajah.cariSpLagu': 'Spotify で曲名・歌手を検索…',
  'jelajah.lencanaKaraoke': 'カラオケ',
  'karaoke.mencari': 'カラオケ版を探しています…',
  'karaoke.diputar': '🎤「{judul}」カラオケ版 — 再生中',
  'karaoke.masuk': '🎤「{judul}」カラオケ版 — 予約しました',
  'karaoke.kuota': '本日の YouTube 検索枠を使い切りました。YouTube タブか Top ElGroup から選んでください。',
  'karaoke.kunci': 'この店舗ではカラオケ版検索がまだ有効ではありません。',
  'karaoke.tidakAda': '「{judul}」のカラオケ版が見つかりません。',
  'isi.diputarSekarang': '「{judul}」を再生中',
  'bilah.pilihLagu': '曲をタップするとすぐ再生',
  'bilah.menungguSesi': 'セッション開始後に再生されます',
  'bilah.kosongSub': '予約はありません',
  'bilah.bahasa': '言語',
  'bilah.nama': '歌う人の名前（任意・スコアボード用）',
  'bilah.kontrol': '操作',
  'bilah.tutup': '閉じる',
  'kontrol.dijeda': '一時停止中',
  'kontrol.posisi': '再生位置',
  'kontrol.perkiraan': '推定値 — Spotify はカラオケ用PCのアプリで再生中',
  'kontrol.keterangan': '説明',
}

const ko: Record<Kunci, string> = {
  'perangkat.tablet': '룸 태블릿',
  'perangkat.belumTerhubung': '태블릿이 연결되지 않았습니다',
  'perangkat.menunggu': '세션 시작을 기다리는 중',
  'perangkat.petunjuk': '이 룸의 예약이 시작되면 화면이 자동으로 열립니다. 코드를 입력할 필요가 없습니다.',
  'perangkat.lepas': '태블릿 연결 해제',
  'perangkat.lepasTanya': '이 태블릿과 룸의 연결을 해제할까요? 리모컨이 다시 코드를 요청합니다.',

  'gerbang.judul': '노래방',
  'gerbang.petunjuk': 'TV 화면에 표시된 코드를 입력하세요.',
  'gerbang.pendek': '코드가 너무 짧습니다',
  'gerbang.masuk': '입장',

  'isi.hai': '안녕하세요',
  'isi.haiNama': '{nama}님, 안녕하세요',
  'isi.room': '룸',
  'isi.dijeda': '일시정지',
  'isi.habis': '시간 종료',
  'isi.kode': '코드',
  'isi.namaKamu': '이름',
  'isi.gantiKode': '코드 변경',
  'isi.masukAntrean': "'{judul}' 예약 완료",

  'tab.jelajah': '둘러보기',
  'tab.antre': '예약',
  'tab.layanan': '서비스',
  'tab.skor': '점수',
  'papan.judul': '순위표',

  'status.belum': '대기 중',
  'status.diproses': '준비 중',
  'status.diantar': '서빙 완료 ✓',
  'layanan.dipanggil': '직원을 호출했습니다 ✓ 곧 룸으로 갑니다.',
  'layanan.sudahDipanggil': '이미 직원을 호출했습니다. 잠시만 기다려 주세요.',
  'layanan.menuju': '직원이 가는 중입니다',
  'layanan.panggil': '직원 호출',
  'layanan.marketing': '담당 매니저',
  'layanan.pesanan': '이 룸의 주문',
  'layanan.kosong': '아직 음식·음료 주문이 없습니다.',
  'layanan.belumDatang': '아직 도착 전',
  'layanan.sudahDatang': '도착 완료',
  'layanan.tanpaMarketing': '이 룸에는 담당 매니저가 없습니다. 직원을 호출해 주세요.',
  'layanan.menungguWaitress': '직원을 호출했습니다 — 확인 대기 중',
  'layanan.panggilMarketing': '담당 매니저 호출',
  'layanan.marketingDipanggil': '담당 매니저를 호출했습니다 ✓ 확인 대기 중입니다.',
  'layanan.marketingSudah': '이미 담당 매니저를 호출했습니다. 잠시만 기다려 주세요.',
  'layanan.marketingMenunggu': '담당 매니저 호출됨 — 확인 대기 중',
  'layanan.marketingMenuju': '담당 매니저가 가는 중입니다',
  'layanan.marketingTakAda': '이 룸의 담당 매니저가 아직 시스템에 연결되지 않았습니다. WhatsApp으로 연락해 주세요.',

  'promo.event': 'ElGroup 이벤트',
  'promo.promo': 'ElGroup 프로모션',
  'promo.sambut': 'El Karaoke에 오신 것을 환영합니다',
  'promo.lihat': '프로모션 보기',
  'promo.petunjuk': 'Spotify, YouTube, ElGroup 인기곡에서 노래를 탭하면 TV에서 바로 재생됩니다.',

  'jelajah.judul': '인기곡',
  'jelajah.cari': '노래 검색',
  'jelajah.ketSpotify': '인도네시아 Top 50',
  'jelajah.ketYoutube': '인기 급상승 음악',
  'jelajah.ketKami': '이곳에서 가장 많이 부른 곡',
  'jelajah.ketCari': '제목 또는 가수',
  'jelajah.namaSkor': '이름 (순위표용)',
  'jelajah.tidakCocok': '일치하는 노래가 없습니다.',
  'jelajah.ketik': '노래 제목이나 가수 이름을 입력하세요.',
  'jelajah.lirik': '가사',
  'jelajah.skorNada': '음정 채점',
  'jelajah.memuat': '불러오는 중…',
  'jelajah.kamiKosong': '최근 60일 동안 부른 노래가 없어요. 첫 곡의 주인공이 되어 보세요!',
  'jelajah.chartKosong': '오늘의 차트가 아직 없습니다.',
  'jelajah.diperbarui': '업데이트 {waktu}',
  'jelajah.belumKatalog': '아직 미등록',

  'antre.judul': '예약 목록',
  'antre.jumlah': '{n}곡',
  'antre.geser': '길게 눌러 순서 변경',
  'antre.kosong': '예약된 노래가 없습니다. 아무 노래나 탭하면 바로 재생됩니다.',
  'antre.setelan': '키 / 템포 / 보컬',
  'antre.batalkan': '취소',
  'antre.sedang': '재생 중',
  'antre.belumPutar': '재생 중인 곡 없음',
  'antre.berikutnya': '다음 곡: {judul}',
  'antre.pilihKiri': '왼쪽에서 노래를 고르세요',
  'antre.selesai': '종료',
  'antre.lewati': '건너뛰기',
  'antre.putar': '지금 재생',
  'kontrol.ulang': '이 곡 반복',
  'kontrol.ulangNyala': '반복: 켜짐',
  'kontrol.ulangMati': '반복: 꺼짐',
  'kontrol.lirik': '가사',
  'kontrol.lirikPas': '가사 싱크 맞음',
  'kontrol.lirikGeser': '가사 {n}초',
  'kontrol.lirikKet': 'YouTube 가사는 자동 준비: 노래방 PC가 영상을 듣고(Whisper) 공식 가사를 맞춥니다. 화면에 가사가 있는 영상은 겹치지 않음. 보통 대기열 등록 후 1–2분 내 준비. 어긋나면 여기서 조정, 모든 룸에 저장.',
  'kontrol.lirikNyala': '가사 켬',
  'kontrol.lirikMati': '가사: 준비 중…',
  'kontrol.lirikBelum': 'TV가 아직 이 영상의 가사를 가져오지 않았습니다. 곡이 시작된 후 다시 시도하세요',
  'antre.mulai': "'{judul}' 시작",

  'setelan.nada': '키',
  'setelan.tempo': '템포',
  'setelan.normal': '보통',
  'setelan.vokal': '보컬',
  'setelan.vokalNyala': '가이드 보컬 켜짐',
  'setelan.vokalMati': '가이드 보컬 꺼짐',
  'setelan.tanpaInstrumental': '이 곡은 아직 별도의 반주 트랙이 없습니다. 센터 채널을 제거하는 방식으로 보컬을 지우기 때문에 베이스와 스네어도 약하게 들립니다.',
  'setelan.acuan1': '아직 기준 멜로디가 없어 점수는 ',
  'setelan.acuanIrama': '리듬 정확도',
  'setelan.acuan2': '로 매겨집니다 (음정은 채점하지 않음).',
  'setelan.batalAntre': '예약 취소',
  "setelan.tempoTidakYt": "YouTube에서는 사용할 수 없음",
  "setelan.ytCatatan": "TV가 EL Audio가 켜진 PC일 때 YouTube 키·보컬 설정이 적용됩니다. 보컬 끄기 = 보컬 감소(곡에 따라 다름). 반주 끄기 = 음소거.",
  'nada.asli': '원키',

  // 3 Okt 2026 — katalog luar (Spotify/YouTube) + kontrol audio pemutar
  "luar.ketSpotify": "카테고리 · 앨범 · 검색",
  "luar.ketYoutube": "뮤직비디오를 검색해 TV에서 재생",
  "luar.cariSpotify": "노래, 앨범, 아티스트 검색…",
  "luar.cariYoutube": "YouTube 검색…",
  "luar.cari": "검색",
  "luar.karaoke": "노래방",
  "luar.tanpaKunciSp": "Spotify 키 없음: 엄선된 플레이리스트로 카테고리, 검색은 iTunes 사용.",
  "luar.tanpaKunciYt": "YouTube 검색이 아직 비활성입니다(API 키 미설정). 차트는 재생할 수 있습니다.",
  "luar.lagu": "노래",
  "luar.album": "앨범",
  "luar.artis": "아티스트",
  "luar.kembali": "뒤로",
  "luar.kosong": "아직 내용이 없습니다.",
  "luar.putarSpotify": "Spotify에서 재생",
  "luar.putarTv": "TV에서 재생",
  "luar.nyanyiKaraoke": "부르기(노래방)",
  "luar.cariVersiKaraoke": "노래방 버전 찾기",
  "luar.adaKatalog": "노래방 버전 있음",
  "luar.chart": "차트",
  "luar.hasil": "검색 결과",
  "luar.masukSpotify": "{judul} 예약됨 — 노래방 컴퓨터의 Spotify에서 재생",
  "luar.masukYoutube": "{judul} 예약됨 (YouTube)",
  "luar.kuotaCatatan": "검색을 누르세요 — 검색 횟수가 제한됩니다.",
  "luar.karaokeTop": "✓ 노래방 켜짐: 목록의 곡을 누르면 → 노래방 버전이 재생됩니다.",
  "luar.wantlist": "구매 목록",
  "luar.tidakAdaHasil": "결과가 없습니다.",
  "kontrol.judul": "오디오 제어",
  "kontrol.vokal": "보컬",
  "kontrol.musik": "반주",
  "kontrol.nada": "키",
  "kontrol.on": "켜기",
  "kontrol.off": "끄기",
  "kontrol.reset": "초기화",
  "kontrol.jeda": "일시정지",
  "kontrol.lanjut": "계속",
  "kontrol.volume": "볼륨",
  "kontrol.hanyaUpload": "업로드한 노래만",
  "kontrol.belumDiproses": "아직 처리되지 않음",
  "kontrol.spotifyLokal": "노래방 컴퓨터의 Spotify 앱에서 재생 — 그곳에서 조절하세요",
  "kontrol.tukarMusik": "보컬 음원 없음 — 반주 끄기는 전체 음소거",
  "kontrol.muteYt": "반주 끄기 = 음소거",
  "kontrol.bukaSpotify": "Spotify에서 열기",
  "kontrol.ytPerluElAudio": "YouTube 보컬·키 조절은 EL Audio 확장 프로그램이 켜진 PC TV에서만 가능합니다",
  "kontrol.ytPengurang": "YouTube: 보컬 끄기는 보컬 감소 처리입니다 — 결과는 곡에 따라 다릅니다",

  'papan.kosong1': '오늘 밤 아직 점수가 없습니다.',
  'papan.kosong2': '노래를 다 부르면 점수가 표시됩니다.',
  'papan.baris': '{lagu}곡 · 평균 {rerata}',
  'papan.campur': '채점 방식 혼합',

  // 5 Okt 2026 — remote: kategori bahasa, karaoke satu ketukan, bilah mengambang
  'kat.id': '인도네시아',
  'kat.en': '영어',
  'kat.zh': '중국어',
  'kat.ja': '일본',
  'kat.ko': '한국',
  'kat.semua': '전체',
  'genre.top': '인기',
  'genre.pilih': '장르 선택',
  'genre.belum': '이 장르 목록은 아직 준비 중입니다. 잠시 후 다시 시도하거나 "인기"를 선택해 주세요.',
  'jelajah.ketSp2': 'Spotify 차트 · 탭하면 노래방 버전으로',
  'jelajah.ketYt2': 'YouTube 인기 음악 · 탭하면 TV에서 재생',
  'jelajah.ketCari2': '노래방 카탈로그에서 검색',
  'jelajah.cariSpLagu': 'Spotify에서 제목이나 가수 검색…',
  'jelajah.lencanaKaraoke': '노래방',
  'karaoke.mencari': '노래방 버전을 찾는 중…',
  'karaoke.diputar': '🎤 "{judul}" 노래방 버전 — 지금 재생 중',
  'karaoke.masuk': '🎤 "{judul}" 노래방 버전 — 예약됨',
  'karaoke.kuota': '오늘 YouTube 검색 한도를 모두 사용했습니다. YouTube 탭이나 Top ElGroup에서 골라 주세요.',
  'karaoke.kunci': '이 매장에서는 아직 노래방 버전 검색이 활성화되지 않았습니다.',
  'karaoke.tidakAda': '"{judul}"의 노래방 버전을 찾지 못했습니다.',
  'isi.diputarSekarang': '"{judul}" 지금 재생 중',
  'bilah.pilihLagu': '아무 노래나 탭하면 바로 재생',
  'bilah.menungguSesi': '세션이 시작되면 재생됩니다',
  'bilah.kosongSub': '예약 없음',
  'bilah.bahasa': '언어',
  'bilah.nama': '부르는 사람 이름 (선택, 점수판용)',
  'bilah.kontrol': '컨트롤',
  'bilah.tutup': '닫기',
  'kontrol.dijeda': '일시정지',
  'kontrol.posisi': '재생 위치',
  'kontrol.perkiraan': '추정치 — Spotify는 노래방 컴퓨터의 앱에서 재생됩니다',
  'kontrol.keterangan': '안내',
}

const KAMUS: Record<KodeBahasa, Record<Kunci, string>> = { id, en, zh, ja, ko }

// ── PENYEDIA + HOOK ────────────────────────────────────────────────────────

const KUNCI_SIMPAN = 'karaoke.bahasa'

type Isi = Record<string, string | number>

export type NilaiBahasa = {
  b: KodeBahasa
  setB: (b: KodeBahasa) => void
  t: (kunci: Kunci, isi?: Isi) => string
}

function buatT(b: KodeBahasa): NilaiBahasa['t'] {
  const kamus = KAMUS[b]
  return (kunci, isi) => {
    // Cadangan berlapis: bahasa terpilih → Indonesia → kuncinya sendiri.
    let teks: string = kamus[kunci] ?? id[kunci] ?? kunci
    if (isi) for (const [k, v] of Object.entries(isi)) teks = teks.split(`{${k}}`).join(String(v))
    return teks
  }
}

// Nilai bawaan BUKAN galat: komponen yang kebetulan dirender di luar penyedia
// tetap tampil (Bahasa Indonesia), bukan membuat layar tamu putih.
const Konteks = createContext<NilaiBahasa>({ b: BAHASA_BAWAAN, setB: () => {}, t: buatT(BAHASA_BAWAAN) })

export function PenyediaBahasa({ children }: { children: ReactNode }) {
  const [b, setBState] = useState<KodeBahasa>(() => {
    try { return bahasaSah(localStorage.getItem(KUNCI_SIMPAN)) } catch { return BAHASA_BAWAAN }
  })

  const setB = useCallback((baru: KodeBahasa) => {
    const sah = bahasaSah(baru)
    setBState(sah)
    try { localStorage.setItem(KUNCI_SIMPAN, sah) } catch { /* privat / penuh */ }
  }, [])

  // lang di <html>: fon CJK yang benar, pembaca layar, dan Chrome tidak
  // menawarkan "terjemahkan halaman" untuk halaman yang sudah berbahasa tamu.
  useEffect(() => {
    try { document.documentElement.lang = cariBahasa(b).locale } catch { /* abaikan */ }
  }, [b])

  const nilai = useMemo<NilaiBahasa>(() => ({ b, setB, t: buatT(b) }), [b, setB])
  return <Konteks.Provider value={nilai}>{children}</Konteks.Provider>
}

export const useBahasa = (): NilaiBahasa => useContext(Konteks)

// ── PEMILIH BAHASA — pil kecil (bendera + kode) + daftar endonim ───────────
//
// Meniru PilihBahasaMasuk di member-app/src/pages/LoginPage.tsx.
export function PilihBahasa({ className = '' }: { className?: string }) {
  const { b, setB } = useBahasa()
  const [buka, setBuka] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const kini = cariBahasa(b)

  useEffect(() => {
    if (!buka) return
    const tutup = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setBuka(false)
    }
    document.addEventListener('mousedown', tutup)
    document.addEventListener('touchstart', tutup)
    return () => {
      document.removeEventListener('mousedown', tutup)
      document.removeEventListener('touchstart', tutup)
    }
  }, [buka])

  return (
    <div ref={ref} className={`relative shrink-0 ${className}`}>
      <button type="button" onClick={() => setBuka(v => !v)} aria-expanded={buka} aria-label="Language"
        className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5
                   text-[12px] tracking-wide text-text hover:bg-white/10">
        <span className="text-[15px] leading-none">{kini.bendera}</span>
        <span>{kini.kode.toUpperCase()}</span>
        <span className="text-[9px] text-text-3">▼</span>
      </button>

      {buka && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[190px] overflow-hidden rounded-xl
                        border border-white/10 bg-bg-1 shadow-[0_12px_30px_rgba(0,0,0,0.45)]">
          {DAFTAR_BAHASA.map(x => (
            <button key={x.kode} type="button" lang={x.locale}
              onClick={() => { setB(x.kode); setBuka(false) }}
              className={`flex w-full items-center gap-2.5 border-t border-white/5 first:border-t-0 px-3 py-2.5 text-left
                          ${x.kode === b ? 'bg-gold/10' : 'hover:bg-white/5'}`}>
              <span className="text-[20px] leading-none">{x.bendera}</span>
              <span className="flex-1 text-[13.5px] text-text">{x.nama}</span>
              {x.kode === b && <span className="text-[13px] text-gold">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
